import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import InputForm from '../../../components/inputForm/InputForm';
import { callUIAPI } from '../../../utils/apiUtils';
import { showErrorMessage, showSuccessMessage } from '@/shared/components/toast-system';
import { ROUTE_BACK_OFFICE_USERS } from '../../../constants';
import * as dbConstants from '../../../dao/dbConstants';
import * as compConstants from '../../../components/componentConstants';

const layoutType = compConstants.INPUT_LAYOUT_TYPE_SEPARATE_LABEL;

const BackofficeUser = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const userId = !!id ? parseInt(id) : 0;

  const [record, setRecord] = useState(null);
  const [originalUpdatedOn, setOriginalUpdatedOn] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  const BACKOFFICE_USER_CONFIG = {
    sections: [
      {
        heading: '',
        inputs: [
          {
            fieldName: 'firstName',
            label: 'First Name',
            layoutType,
            validationRules: { isRequired: true, maxLength: 100 }
          },
          {
            fieldName: 'lastName',
            label: 'Last Name',
            layoutType,
            validationRules: { isRequired: false, maxLength: 100 }
          },
          {
            fieldName: 'email',
            label: 'Email',
            layoutType,
            validationRules: { isRequired: true, maxLength: 100, isEmail: true },
            isDisabled: () => !isNew
          },
          {
            fieldName: 'isActive',
            label: 'Active?',
            inputType: compConstants.INPUT_TYPE_CHECKBOX,
            layoutType: compConstants.INPUT_LAYOUT_TYPE_INLINE,
            isHidden: () => isNew
          }
        ]
      }
    ]
  };

  useEffect(() => {
    if (!isNew) {
      setLoading(true);
      callUIAPI(`bo/backofficeusers/${id}`, null, 'get', true)
        .then((response) => {
          const user = response.data.item;
          setRecord({
            id: user.id,
            firstName: user.firstName || '',
            lastName: user.lastName || '',
            email: user.email || '',
            isActive: user.isActive ?? true,
          });
          setOriginalUpdatedOn(user.updatedOn);
        })
        .catch(() => {
          showErrorMessage('Could not load user.');
          navigate(ROUTE_BACK_OFFICE_USERS);
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setRecord({
        firstName: '',
        lastName: '',
        email: '',
        isActive: true,
      });
    }
  }, [id, isNew, navigate]);

  const handleSubmit = async (entityName, values) => {
    setSaving(true);
    try {
      if (isNew) {
        await callUIAPI('bo/backofficeusers', values, 'post', true);
        showSuccessMessage('User created. An invite email has been sent.');
      } else {
        const payload = {
          ...values,
          id: userId,
          updatedOn: originalUpdatedOn
        };
        await callUIAPI('bo/backofficeusers', payload, 'put', true);
        showSuccessMessage('User updated successfully.');
      }
      navigate(ROUTE_BACK_OFFICE_USERS);
    } catch (error) {
      const msg = error?.response?.data?.message || (isNew ? 'Could not create user.' : 'Could not update user.');
      showErrorMessage(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="container mt-4">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <InputForm
      entityName={dbConstants.ENTITY_NAME_BACK_OFFICE_USER || 'BackofficeUser'}
      inputConfig={BACKOFFICE_USER_CONFIG}
      heading="Team Member"
      id={userId}
      initialRecord={record}
      initialValues={record}
      defaultValues={record} // Prevents fetchDefaultValues from executing on mounts with id=0
      loading={loading}
      saving={saving}
      beforeSave={handleSubmit}
      doNotSave={true}
      backRoute={ROUTE_BACK_OFFICE_USERS}
    />
  );
};

export default BackofficeUser;